import { APP_TERMS_SECTIONS } from "./appTerms";

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function formatAcceptedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function renderSection(section) {
  const paragraphs = (section.paragraphs || [])
    .map((text) => `<p>${escapeHtml(text)}</p>`)
    .join("");
  const bullets = (section.bullets || []).length
    ? `<ul>${section.bullets
        .map((text) => `<li>${escapeHtml(text)}</li>`)
        .join("")}</ul>`
    : "";
  const after = (section.after || [])
    .map((text) => `<p>${escapeHtml(text)}</p>`)
    .join("");
  return `
    <h2>${escapeHtml(section.title)}</h2>
    ${paragraphs}
    ${bullets}
    ${after}
  `;
}

export function formatOperatorTermsAcceptedAt(value) {
  return formatAcceptedAt(value);
}

function clauseList(clauses) {
  return Array.isArray(clauses) ? clauses.filter((item) => item && item.body) : [];
}

export function buildOperatorTermsPlainText({
  operatorName,
  farmName,
  acceptedAt,
  identification,
  contractClauses,
}) {
  const when = formatAcceptedAt(acceptedAt);
  const acceptedLine = when
    ? `Leu e aceitou os termos no aplicativo em ${when}.`
    : "Ainda não aceitou os termos no aplicativo.";
  const sections = APP_TERMS_SECTIONS.map((section) => {
    const parts = [section.title];
    (section.paragraphs || []).forEach((text) => parts.push(text));
    (section.bullets || []).forEach((text) => parts.push(`• ${text}`));
    (section.after || []).forEach((text) => parts.push(text));
    return parts.join("\n");
  }).join("\n\n");
  const extras = clauseList(contractClauses);
  const extraText = extras.length
    ? [
        "",
        "Contrato de uso (cláusulas acrescentadas na plataforma)",
        ...extras.map((item, index) => {
          const stamp = formatAcceptedAt(item.created_at);
          const title = stamp
            ? `Cláusula ${index + 1} — acrescentada em ${stamp}`
            : `Cláusula ${index + 1}`;
          return `${title}\n${item.body}`;
        }),
      ]
    : [];
  return [
    "AgriConnected — Termo de aceite",
    `Operador: ${operatorName || ""}`,
    `Identificação: ${identification || ""}`,
    `Fazenda: ${farmName || ""}`,
    `Aceite: ${acceptedLine}`,
    "",
    sections,
    ...extraText,
  ].join("\n");
}

export function exportOperatorTermsAcceptance(details) {
  if (typeof document === "undefined") return false;
  const text = buildOperatorTermsPlainText(details);
  const slug = String(details.identification || details.operatorName || "operador")
    .replace(/[^\w\-]+/g, "-")
    .replace(/-+/g, "-");
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `termo-aceite-${slug || "operador"}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
  return true;
}

export async function copyOperatorTermsAcceptance(details) {
  const text = buildOperatorTermsPlainText(details);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  return false;
}

export function printOperatorTermsAcceptance({
  operatorName,
  farmName,
  acceptedAt,
  identification,
  contractClauses,
}) {
  if (typeof window === "undefined") return false;
  const when = formatAcceptedAt(acceptedAt);
  const acceptedLine = when
    ? `Leu e aceitou os termos no aplicativo em ${when}.`
    : "Ainda não aceitou os termos no aplicativo. Espaço para assinatura física no escritório.";
  const extras = clauseList(contractClauses)
    .map((item, index) => {
      const stamp = formatAcceptedAt(item.created_at);
      const title = stamp
        ? `Cláusula ${index + 1} — acrescentada em ${stamp}`
        : `Cláusula ${index + 1}`;
      return `<h2>${escapeHtml(title)}</h2><p>${escapeHtml(item.body).replace(
        /\n/g,
        "<br />"
      )}</p>`;
    })
    .join("");
  const extraBlock = extras
    ? `<h2>Contrato de uso (cláusulas acrescentadas na plataforma)</h2>${extras}`
    : "";
  const body = APP_TERMS_SECTIONS.map(renderSection).join("") + extraBlock;
  const html = `<!DOCTYPE html>
<html lang="pt-BR">
  <head>
    <meta charset="utf-8" />
    <title>Termo de aceite — ${escapeHtml(operatorName)}</title>
    <style>
      body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; margin: 32px; line-height: 1.45; }
      h1 { font-size: 22px; margin-bottom: 8px; }
      h2 { font-size: 16px; margin-top: 22px; }
      p, li { font-size: 13px; }
      .meta { margin: 16px 0 24px; padding: 12px; border: 1px solid #ccc; }
      .sign { margin-top: 36px; }
      .line { margin-top: 36px; border-top: 1px solid #333; width: 320px; padding-top: 6px; font-size: 12px; }
    </style>
  </head>
  <body>
    <h1>AgriConnected — Termo de aceite</h1>
    <div class="meta">
      <p><strong>Operador:</strong> ${escapeHtml(operatorName)}</p>
      <p><strong>Identificação:</strong> ${escapeHtml(identification)}</p>
      <p><strong>Fazenda:</strong> ${escapeHtml(farmName)}</p>
      <p><strong>Aceite:</strong> ${escapeHtml(acceptedLine)}</p>
    </div>
    ${body}
    <div class="sign">
      <p>Assinatura física no escritório, se necessária:</p>
      <div class="line">Assinatura do colaborador</div>
      <div class="line">Data</div>
    </div>
  </body>
</html>`;

  const popup = window.open("", "_blank");
  if (!popup) return false;
  popup.document.open();
  popup.document.write(html);
  popup.document.close();
  popup.focus();
  popup.print();
  return true;
}
